import type { FC, ReactNode } from "react";
import { IconChip, type IconChipColor } from "./IconChip";
import { cx } from "../utils/cx";

export interface SectionCardProps {
    title?: ReactNode;
    description?: ReactNode;
    /** Buttons at the inline-end of the header. */
    actions?: ReactNode;
    icon?: FC<{ className?: string }>;
    iconColor?: IconChipColor;
    footer?: ReactNode;
    children?: ReactNode;
    className?: string;
    bodyClassName?: string;
    /** id of the heading, for aria-labelledby. */
    headingId?: string;
}

/** The standard white card with a header (Untitled UI card anatomy), used by every page section. */
export const SectionCard = ({
    title,
    description,
    actions,
    icon,
    iconColor = "brand",
    footer,
    children,
    className,
    bodyClassName,
    headingId,
}: SectionCardProps) => (
    <section
        className={cx("flex flex-col rounded-2xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", className)}
        aria-labelledby={title ? headingId : undefined}
    >
        {(title || actions) && (
            <header className="flex flex-wrap items-start gap-4 border-b border-secondary px-4 py-5 md:px-6">
                {icon && <IconChip icon={icon} color={iconColor} size="md" />}
                <div className="flex min-w-[min(100%,14rem)] flex-1 flex-col gap-1">
                    {title && (
                        <h2 id={headingId} className="text-lg font-semibold text-primary">
                            {title}
                        </h2>
                    )}
                    {description && <p className="text-sm text-tertiary">{description}</p>}
                </div>
                {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
            </header>
        )}
        <div className={cx("flex flex-col gap-5 px-4 py-5 md:px-6", bodyClassName)}>{children}</div>
        {footer && <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-secondary px-4 py-4 md:px-6">{footer}</footer>}
    </section>
);

export interface KeyValueItem {
    label: ReactNode;
    value: ReactNode;
    hint?: ReactNode;
    /** Bold separator line above (totals). */
    emphasis?: boolean;
}

/** Label / value rows (receipts, previews, details). Values sit at the inline-end. */
export const KeyValueList = ({ items, className }: { items: KeyValueItem[]; className?: string }) => (
    <dl className={cx("flex flex-col gap-3", className)}>
        {items.map((item, i) => (
            <div key={i} className={cx("flex items-start justify-between gap-4 text-sm", item.emphasis && "mt-1 border-t border-secondary pt-3 font-semibold")}>
                <dt className={cx("flex min-w-0 flex-col gap-0.5", item.emphasis ? "text-primary" : "text-tertiary")}>
                    <span>{item.label}</span>
                    {item.hint && <span className="text-xs text-quaternary">{item.hint}</span>}
                </dt>
                <dd className={cx("shrink-0 text-end digits-tabular", item.emphasis ? "text-primary" : "text-secondary")}>{item.value}</dd>
            </div>
        ))}
    </dl>
);

/** Inline callout for explanations: info (brand), warning, success, error. */
export const Callout = ({
    tone = "brand",
    icon: Icon,
    title,
    children,
    className,
}: {
    tone?: "brand" | "warning" | "success" | "error" | "gray";
    icon?: FC<{ className?: string }>;
    title?: ReactNode;
    children?: ReactNode;
    className?: string;
}) => {
    const tones = {
        brand: "bg-brand-primary_alt ring-brand-200 text-brand-secondary dark:bg-brand-500/10 dark:ring-brand-500/30 dark:text-brand-300",
        warning: "bg-warning-primary ring-utility-yellow-200 text-warning-primary dark:bg-yellow-500/10 dark:ring-yellow-500/25",
        success: "bg-success-primary ring-utility-green-200 text-success-primary dark:bg-green-500/10 dark:ring-green-500/25",
        error: "bg-error-primary ring-error_subtle text-error-primary dark:bg-red-500/10 dark:ring-red-500/25",
        gray: "bg-secondary ring-secondary text-secondary",
    } as const;
    return (
        <div className={cx("flex gap-3 rounded-xl p-4 ring-1 ring-inset", tones[tone], className)}>
            {Icon && <Icon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />}
            <div className="flex min-w-0 flex-col gap-1 text-sm">
                {title && <p className="font-semibold">{title}</p>}
                {children && <div className="text-secondary">{children}</div>}
            </div>
        </div>
    );
};
